/**
 * `useTenantConfig` and `useTenantRetry` hooks.
 *
 * `useTenantConfig` exposes the current `TenantStatus`. Consumers should
 * narrow on `state.kind` rather than peeking at fields that only exist
 * in some variants — TypeScript will refuse anything else.
 *
 * `useTenantRetry` returns the imperative retry callback the error
 * fallback wires to its "Try again" button. It is a no-op if the
 * provider tree is missing (defensive default in `TenantRetryContext`).
 */

import { useContext } from 'react';
import {
  TenantConfigContext,
  TenantRefreshContext,
  TenantRetryContext,
} from './TenantConfigProvider.js';
import type { TenantStatus } from './states.js';

export function useTenantConfig(): TenantStatus {
  return useContext(TenantConfigContext);
}

export function useTenantRetry(): () => void {
  return useContext(TenantRetryContext);
}

/**
 * Imperative refresh of the runtime config. Used by the engine client
 * provider to recover from a 401 caused by a rotated `publicApiKey`
 * without forcing the visitor to reload the tab. Internally
 * debounced — see `REFRESH_DEBOUNCE_MS` in TenantConfigProvider.
 */
export function useTenantRefresh(): () => void {
  return useContext(TenantRefreshContext);
}
